import {
  SYSTEM_ROLES,
  ROLE_FIELD,
  SYSTEM_ROLE_OPTIONS,
} from '../constants/roles';
import { PERMISSION_SCOPES } from '../constants/permissions';
import { PARAMS } from '../constants/pages';
import { FIELD_TYPES } from '../constants/fieldTypes';
import {
  isArrayEmpty,
  isObjectEmpty,
  getItemById,
  mapObjectsArrayFromNumbersArray,
} from './helpers';
import {
  isEmpty,
  forbiddenCharacters,
  validateLength,
  validateFields,
} from './validation';

const { ID, ROLE, JOB_TITLE, TRACK, LEVEL, DIVISION, DEPARTMENT } = PARAMS;

export const prepareRoles = (
  labels,
  roles,
  shouldRemoveExternal = false,
  shouldRemoveAdditional = false
) =>
  roles.reduce((acc, role) => {
    if (shouldRemoveExternal && role.name === SYSTEM_ROLES.ASSOCIATE)
      return acc;
    if (shouldRemoveAdditional && !role.is_system) return acc;
    acc.push({
      ...role,
      label: role.is_system ? labels[role.name] : role.name,
    });
    return acc;
  }, []);

export const prepareSystemRoleFilters = systemRoles =>
  systemRoles.map(sRole => ({ ...sRole, id: sRole.name }));

export const systemRoleCheck = (user, role) => {
  const [userRole] = user?.roles || [];
  return userRole === role;
};

export const prepareAccessMatrixRoles = (labels, roles) => {
  return roles.reduce((acc, role) => {
    const isAdminRole = role.name === SYSTEM_ROLES.ADMIN;
    const isModeratorRole = role.name === SYSTEM_ROLES.MODERATOR;
    const isEmployeeRole = role.name === SYSTEM_ROLES.USER;
    const isAssociateRole = role.name === SYSTEM_ROLES.ASSOCIATE;
    const isAdditionalRole = !role.is_system;
    const isSystemRoleExpandable = !isAdditionalRole && isAdminRole;
    const isSystemRoleEditable =
      !isAdditionalRole && (isModeratorRole || isEmployeeRole);
    const hasPermissionsWithMultipleScopes =
      !isAdditionalRole && isModeratorRole;
    if (role.parent) return acc;

    return [
      ...acc,
      {
        ...role,
        label: isAdditionalRole ? role.name : labels.systemRoles[role.name],
        isDisabled: !isAdditionalRole && !isSystemRoleEditable,
        isExpandable: isSystemRoleExpandable,
        hasPermissionsWithMultipleScopes,
        isAdditionalRole,
        permissionScopes: [
          ...(isAdminRole
            ? [
                {
                  label: labels?.permissionScopes?.global?.label,
                  tooltip: labels?.permissionScopes?.global?.tooltip,
                  value: PERMISSION_SCOPES.GLOBAL,
                },
              ]
            : []),
          ...(isModeratorRole || isEmployeeRole
            ? [
                {
                  label: labels?.permissionScopes?.self?.label,
                  tooltip: labels?.permissionScopes?.self?.tooltip,
                  value: PERMISSION_SCOPES.SELF,
                },
              ]
            : []),
          ...(isModeratorRole
            ? [
                {
                  label: labels?.permissionScopes?.directReports?.label,
                  tooltip: labels?.permissionScopes?.directReports?.tooltip,
                  value: PERMISSION_SCOPES.DR,
                },
              ]
            : []),
          ...(isModeratorRole
            ? [
                {
                  label: labels?.permissionScopes?.indirectReports?.label,
                  tooltip: labels?.permissionScopes?.indirectReports?.tooltip,
                  value: PERMISSION_SCOPES.IDR,
                },
              ]
            : []),
          ...(isAdditionalRole || isAssociateRole
            ? [
                {
                  label: labels?.permissionScopes?.custom?.label,
                  tooltip: labels?.permissionScopes?.custom?.tooltip,
                  value: PERMISSION_SCOPES.CUSTOM,
                },
              ]
            : []),
        ],
      },
    ];
  }, []);
};

export const preparePermissionScopeValues = (
  isGlobalScopePermission,
  permissionScopes
) =>
  isGlobalScopePermission
    ? [PERMISSION_SCOPES.GLOBAL]
    : permissionScopes.map(scope => scope.value);

export const validateAdditionalRoles = async additionalRoles => {
  const errors = {};

  for (let i = 0; i < additionalRoles.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const roleErrors = await validateFields(ROLE_FIELD, additionalRoles[i]);

    if (!isObjectEmpty(roleErrors)) {
      errors[i] = roleErrors;
    }
  }

  return !isObjectEmpty(errors) ? errors : null;
};

export const preparePrimaryRoleFormInitialData = (
  primaryRoleId,
  additionalRoleId
) => ({
  primaryRoleId: primaryRoleId || null,
  additionalRoleId: additionalRoleId || null,
});

export const prepareUsersScopeInitialData = (scope, allUsers = []) => ({
  ...(scope ? { [ID]: scope[ID] } : {}),
  name: scope?.name || '',
  employees: {
    selected: {
      [ID]: scope?.criteria?.[ID] || [],
      [ROLE]: (scope?.criteria?.[ROLE] || []).reduce((roleAcc, roleId) => {
        const roleOption = getItemById(SYSTEM_ROLE_OPTIONS, roleId);
        if (roleOption?.value) {
          return [...roleAcc, roleOption.value];
        }
        return roleAcc;
      }, []),
      [JOB_TITLE]: scope?.criteria?.[JOB_TITLE] || [],
      [TRACK]: scope?.criteria?.[TRACK] || [],
      [LEVEL]: scope?.criteria?.[LEVEL] || [],
      [DIVISION]: scope?.criteria?.[DIVISION] || [],
      [DEPARTMENT]: scope?.criteria?.[DEPARTMENT] || [],
    },
    filteredValues: scope?.members || allUsers,
  },
});

export const prepareUsersScopeFields = (getUsers, peopleFilters = []) => [
  {
    name: 'name',
    type: FIELD_TYPES.TEXT,
    required: true,
    validators: [
      {
        validator: isEmpty,
        type: 'required',
      },
      {
        validator: forbiddenCharacters,
        type: 'forbiddenCharacters',
      },
      {
        validator: fieldValue => validateLength(fieldValue, 0, 255),
        type: 'nameMaxLength',
      },
    ],
  },
  {
    name: 'employees',
    type: FIELD_TYPES.COLLAPSABLE_FILTERS,
    required: true,
    showSelection: true,
    shouldRemoveFieldSpacing: true,
    shouldRemoveFieldError: true,
    shouldCalculateSelectionHeight: true,
    validators: [
      {
        type: 'requiredFilterValues',
        validator: value => !isArrayEmpty(value?.filteredValues),
      },
      {
        type: 'requiredFilter',
        validator: value =>
          Object.keys(value?.selected)?.some(
            filterKey => !isArrayEmpty(value?.selected[filterKey])
          ),
      },
    ],
    getResults: getUsers,
    filters: peopleFilters,
  },
];

export const prepareSystemRoles = allRoles =>
  allRoles.filter(
    role => role?.is_system && role?.name !== SYSTEM_ROLES.ASSOCIATE
  );

export const prepareAdditionalRoleOptions = allRoles =>
  allRoles.filter(role => !role?.is_system);

export const prepareUsersScopeGroups = (groups, availableUsers) => {
  if (isArrayEmpty(groups)) return [];

  return groups.map(group => ({
    ...group,
    members: mapObjectsArrayFromNumbersArray(availableUsers, group.members),
  }));
};

export const prepareSelectedFilters = selectedFilters =>
  Object.keys(selectedFilters).reduce((acc, currentFilterKey) => {
    if (!isArrayEmpty(selectedFilters[currentFilterKey])) {
      const filterValue =
        currentFilterKey === ROLE
          ? selectedFilters[currentFilterKey].reduce((roleAcc, role) => {
              return [
                ...roleAcc,
                getItemById(SYSTEM_ROLE_OPTIONS, role, 'value').id,
              ];
            }, [])
          : selectedFilters[currentFilterKey];
      return { ...acc, [currentFilterKey]: filterValue };
    }
    return acc;
  }, {});
